import { Moment } from '@e3dc-react/shell/Libs/moment'
import { ChartView, FetchingState, RechartData, RechartDataObject, useChartDateBarContext } from 'src/Libs/chart-kit'
import { useThreeSixtySavingsConsumption } from 'src/Libs/dynamic-power-prices/hooks/use-three-sixty-savings-consumption'
import { useStorageIdList } from 'src/Libs/summary-data/hooks/use-storage-id-to-get-list'
import { useFetchData, usePVForecastData } from 'src/Libs/system-data-handler'
import { getSums } from 'src/Libs/system-data-handler/utils/chartFunctions'
import useFetchPocPerformantChartValues from '../../default-charts/hooks/use-fetch-poc-performant-chart-values'

/**
 * Provides PoC chart data including history data, loading state, sums, and weather breakthrough status.
 */
export const usePocData = (from: Moment, to: Moment, storageId: string, view: ChartView): RechartData => {
  useFetchDataByOldLogic(storageId, from, to, view)

  const { pvForecastData } = usePVForecastData(from.toDate(), to.toDate(), storageId, 15)
  const { historyData, isLoading, hasWeatherBreakthrough, ...rest } = useFetchPocPerformantChartValues(
    storageId ?? '',
    from,
    to,
    view,
    pvForecastData
  )

  const storagesList = useStorageIdList(storageId)
  const threeSixtySavingsResolution = view === 'year' || view === 'month' ? 'month' : 'day'
  const threeSixtySavingsTotalConsumption = useThreeSixtySavingsConsumption({
    storagesIds: storagesList,
    from,
    to,
    resolution: threeSixtySavingsResolution,
  })
  const isThreeSixtySavingsLoading = threeSixtySavingsTotalConsumption?.ThreeSixtySavings?.isLoading ?? false

  /** Put the available date in first row it can otherwise break the sum calculation which breaks chart rendering because of missing traces. */
  const historyDataFullestFirst = [...historyData].sort((a, b) => Object.keys(b).length - Object.keys(a).length)
  const sums = getSums(historyDataFullestFirst, [], pvForecastData, view === 'day' ? 4 : 1, threeSixtySavingsTotalConsumption)
  const detailedLoadingStates = Object.values({ ...rest, isThreeSixtySavingsLoading })
  const data: RechartData = {
    data: historyData as RechartDataObject[],
    loadingState: {
      state: isLoading || isThreeSixtySavingsLoading ? FetchingState.IsFetching : FetchingState.Completed,
      fetchedMonths: detailedLoadingStates.filter(state => state === false).length,
      monthAmountToFetch: detailedLoadingStates.length,
    },
    sums,
    hasWeatherBreakthrough,
    isPocChart: true,
  }

  return data
}

/**
 * The poc fetch logic is our new, faster approach. It aggregates data on backend instead of frontend side. However, those new, faster routes do not include forecast values yet.
 * Therefore, as a workaround, we still use the old fetching logic when users activate the forecast. Forecast values are calculated on the frontend side, using redux values.
 * This can not easily be done with the new logic, as the new performant routes do not store the raw data in redux, but only return the aggregated values.
 *
 * In the future, the calculation of the forecast values should be moved to the backend.
 */
const useFetchDataByOldLogic = (storageId: string, from: Moment, to: Moment, view: ChartView): void => {
  const { isForecastEnabled } = useChartDateBarContext()
  const shouldFetch = isForecastEnabled && view === 'day'

  useFetchData({ storageId: shouldFetch ? storageId : '', from, to, view })
}
